import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Snackbar,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Alert } from "@mui/material";
import { selectGameId } from "../../gamestate/gameStateSlice";
import ResetGameButton from "./ResetGameButton";
import { resetAllCaches } from "../../host/momentCacheManager";
import Provider from "provider";

const IS_DEV_OR_STAGING = process.env.REACT_APP_ENV !== "production";

const AdminActions = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("Caches cleared!");
  const [preFinalLoading, setPreFinalLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedToastOpen, setSeedToastOpen] = useState(false);
  const [seedError, setSeedError] = useState(null);
  const gameId = useSelector(selectGameId);

  const handleClearCaches = () => {
    setConfirmOpen(false);
    resetAllCaches();
    setToastMessage("Caches cleared!");
    setToastOpen(true);
  };

  const handleSetPreFinal = async () => {
    setPreFinalLoading(true);
    try {
      await Provider.debugSetPreFinal(gameId);
      resetAllCaches();
      setToastMessage("Game set to pre-Final Jeopardy! Refresh gameboard.");
      setToastOpen(true);
    } catch (error) {
      console.error("Error setting pre-final state:", error);
      setToastMessage("Error: " + error.message);
      setToastOpen(true);
    } finally {
      setPreFinalLoading(false);
    }
  };

  const handleSkipToFinale = async () => {
    if (!gameId) return;
    setSeeding(true);
    setSeedError(null);
    try {
      await Provider.seedFinaleScenario(gameId);
      setSeedToastOpen(true);
    } catch (err) {
      setSeedError(err?.message || "Failed to seed finale scenario");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <React.Fragment>
      <Stack direction="row" spacing={2}>
        <ResetGameButton />
        <Button
          size="large"
          variant="contained"
          color="warning"
          onClick={() => setConfirmOpen(true)}
        >
          Clear Caches
        </Button>
        <Button
          size="large"
          variant="contained"
          color="secondary"
          onClick={handleSetPreFinal}
          disabled={preFinalLoading || !gameId}
          startIcon={preFinalLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {preFinalLoading ? "Setting up..." : "Debug - Pre-Final Round"}
        </Button>
        {IS_DEV_OR_STAGING && (
          <Button
            size="large"
            variant="contained"
            color="secondary"
            onClick={handleSkipToFinale}
            disabled={seeding || !gameId}
            startIcon={seeding ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {seeding ? "Seeding..." : "Skip to Finale"}
          </Button>
        )}
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
      >
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear all frontend caches? This will reset
            all cached narrations and moment data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleClearCaches} color="primary" autoFocus>
            Clear Caches
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toastOpen} autoHideDuration={4000} onClose={() => setToastOpen(false)}>
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setToastOpen(false)}
          severity="success"
        >
          {toastMessage}
        </Alert>
      </Snackbar>

      <Snackbar open={seedToastOpen} autoHideDuration={4000} onClose={() => setSeedToastOpen(false)}>
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSeedToastOpen(false)}
          severity="success"
        >
          Finale scenario seeded!
        </Alert>
      </Snackbar>

      <Snackbar open={!!seedError} autoHideDuration={6000} onClose={() => setSeedError(null)}>
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSeedError(null)}
          severity="error"
        >
          {seedError}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default AdminActions;
